import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import NotAvailable from 'src/assets/img/not-available.png';
import DocumentCountTracker from 'src/components/DocumentCountTracker';
import PreviewPDF from 'src/components/PreviewPDF';
import Button, { ButtonProps } from 'src/components/design-system/Button';
import useConfirm from 'src/hooks/useConfirm';
import useAttachToReceiptOrInvoiceApi from 'src/services/useAttachToReceiptOrInvoiceApi';
import useGetAttachmentsForReceiptApi from 'src/services/useGetAttachmentsForReceiptApi';
import useGetUpdatedReceiptOrInvoiceDetailsApi from 'src/services/useGetUpdatedReceiptOrInvoiceDetailsApi';
import useUnattachDocumentsApi from 'src/services/useUnattachDocumentsApi';
import { ScanManagerInvoiceItem } from 'src/types/ScanManagerInvoices';
import { modifyRequest } from 'src/utils/requestInterceptor';
import Image from './Image';

import AttachExistingDocumentModal from 'src/components/scanner/AttachExistingDocumentModal';
import useBoolean from 'src/hooks/useBoolean';
import useWindowSize from 'src/hooks/useWindowSize';
import { initialScanManagerDataState } from 'src/sections/attach_document/AttachDocumentWizard';
import useDeleteDocumentApi from 'src/services/useDeleteDocumentApi';
import { isEqualString, validSignedUrl } from 'src/utils/strings';
import useGetInvoiceAttachmentsApi from '../services/useGetInvoiceAttachmentsApi';
import UploadFilesModal from './UploadFilesModal';

interface Btn {
  color: ButtonProps['color'];
  text: string;
  btnClick: () => void;
  disable: boolean;
  loading?: boolean;
}

export const updatedScanManagerDataState = (
  receiptId: number,
  isInvoice: boolean,
  take?: number
) => {
  const newState: any = {
    ...initialScanManagerDataState(isInvoice),
    take: take || initialScanManagerDataState(isInvoice).take,
    filter: {
      filters: [
        {
          filters: [{ field: 'receiptId', value: `${receiptId}`, operator: 'eq' }],
        },
      ],
    },
  };
  return modifyRequest(newState);
};

interface ScansPreviewSectionProps {
  status: string;
  receiptId: number;
  isInvoice: boolean;
  multiSelect?: boolean;
  totalDocCount?: number;
  companyId: number;
  refresh: VoidFunction;
  poStatus: string;
  ocrGenerated?: boolean;
}

const PreviewSection: FC<ScansPreviewSectionProps> = ({
  receiptId,
  isInvoice,
  totalDocCount,
  multiSelect = false,
  companyId,
  poStatus,
  refresh,
  ocrGenerated = false,
}) => {
  const {
    value: isAttachModalOpen,
    setTrue: openAttachModal,
    setFalse: closeAttachModal,
  } = useBoolean();
  const [attachments, setAttachments] = useState([] as ScanManagerInvoiceItem[]);
  const [selected, setSelected] = useState(1);
  const {
    value: isUploadOpen,
    setFalse: closeUploadModal,
    setTrue: openUploadModal,
  } = useBoolean();
  const { height, width } = useWindowSize();

  const [selfRefresh, setSelfRefresh] = useState(false);
  const { deleteAttachment, isLoading: isDeleting } = useDeleteDocumentApi(receiptId, isInvoice);
  const isImageType = useMemo(
    () => !attachments?.[selected - 1]?.description?.toLowerCase().endsWith('.pdf'),
    [attachments, selected]
  );
  const { getAttachedReceiptDocuments, isAttachDocumentLoading: isReceiptAttachmentLoading } =
    useGetAttachmentsForReceiptApi(companyId);
  const { getInvoiceAttachments, isInvoiceAttachmentsLoading } =
    useGetInvoiceAttachmentsApi(receiptId);
  const { attachDocumentsToReceiptOrInvoice, isAttachDocumentLoading } =
    useAttachToReceiptOrInvoiceApi(isInvoice);
  const { updateReceiptOrInvoiceDetails } = useGetUpdatedReceiptOrInvoiceDetailsApi(isInvoice);
  const toggleRefresh = useCallback(() => {
    setSelfRefresh((prev) => !prev);
    refresh();
  }, [refresh]);
  const { unAttachDocument, isLoading } = useUnattachDocumentsApi(isInvoice);

  const noDocContent = useMemo(() => {
    let message = 'No Attached Documents';
    if (multiSelect) {
      message = 'Select a Single Record to View Attachments';
    } else if (!receiptId) {
      message = 'Please Select an attachment';
    } else {
      message = '';
    }
    return message;
  }, [multiSelect, receiptId]);

  const getDocsToAttach = useCallback(
    async (attachIds: number[]) => {
      if (attachIds.length > 0) {
        await attachDocumentsToReceiptOrInvoice({ id: receiptId, attachIds }).then(() => {
          closeAttachModal();
          updateReceiptOrInvoiceDetails({ id: receiptId }).then(() => {
            toggleRefresh();
          });
        });
      }
    },
    [
      attachDocumentsToReceiptOrInvoice,
      receiptId,
      closeAttachModal,
      updateReceiptOrInvoiceDetails,
      toggleRefresh,
    ]
  );

  const getAttachments = useCallback(
    async (receiptId: number) => {
      if (isInvoice) {
        const response = await getInvoiceAttachments();
        setAttachments(response || []);
      } else {
        const response = await getAttachedReceiptDocuments({
          body: updatedScanManagerDataState(
            receiptId,
            isInvoice,
            Math.max(totalDocCount || 20, 100)
          ),
        });

        setAttachments(response?.items || []);
      }
      setSelected(1);
    },
    [getAttachedReceiptDocuments, getInvoiceAttachments, isInvoice, totalDocCount]
  );

  useEffect(() => {
    if (multiSelect) {
      setAttachments([]);
    } else if (receiptId) {
      getAttachments(receiptId);
    }
  }, [getAttachments, receiptId, selfRefresh, multiSelect]);

  const unAttachDocuments = useCallback(() => {
    unAttachDocument({
      id: receiptId,
      attachId: attachments[selected - 1]?.attachId,
    }).then(() => {
      toggleRefresh();
      if (attachments.length > 0) setSelected(1);
    });
  }, [receiptId, attachments, selected, toggleRefresh, unAttachDocument]);

  const { confirm } = useConfirm();

  const increment = useCallback(() => {
    setSelected((prev) => prev + 1);
  }, []);

  const decrement = useCallback(() => {
    setSelected((prev) => prev - 1);
  }, []);

  const handleDeleteClick = useCallback(async () => {
    const isDeleteAccepted = await confirm({
      title: 'Delete Document',
      content: <div>Are you sure you want to delete this document(s)?</div>,
      confirmButtonText: 'Delete',
    });
    if (isDeleteAccepted && attachments.length > 0) {
      deleteAttachment({ ids: [attachments[selected - 1].attachId] }).then(() => {
        toggleRefresh();
        if (attachments.length > 0) setSelected(1);
      });
    }
  }, [confirm, attachments, selected, deleteAttachment, toggleRefresh]);

  const disable = useMemo(() => !isEqualString(poStatus, 'open'), [poStatus]);

  const buttons: Btn[] = useMemo(
    () => [
      {
        color: 'primary',
        text: 'Upload',
        btnClick: openUploadModal,
        disable: disable || multiSelect || !receiptId,
      },
      {
        color: 'primary',
        text: 'Attach',
        btnClick: openAttachModal,
        disable: disable || multiSelect || !receiptId,
        loading: isAttachDocumentLoading,
      },
      {
        color: 'dark',
        text: 'Un-attach',
        btnClick: unAttachDocuments,
        disable: disable || attachments.length === 0 || multiSelect || !receiptId,
        loading: isLoading,
      },
      {
        color: 'error',
        text: 'Delete',
        btnClick: handleDeleteClick,
        disable: disable || attachments.length === 0 || multiSelect || !receiptId,
        loading: isDeleting,
      },
    ],
    [
      openUploadModal,
      disable,
      multiSelect,
      receiptId,
      openAttachModal,
      isAttachDocumentLoading,
      unAttachDocuments,
      attachments.length,
      isLoading,
      handleDeleteClick,
      isDeleting,
    ]
  );

  return (
    <>
      <AttachExistingDocumentModal
        isInvoices={isInvoice}
        open={isAttachModalOpen}
        onClose={closeAttachModal}
        onAttach={getDocsToAttach}
        companyId={String(companyId)}
      />

      <UploadFilesModal
        open={isUploadOpen}
        closeModal={closeUploadModal}
        companyId={companyId}
        refreshParent={toggleRefresh}
        isInvoice={isInvoice}
        id={receiptId}
      />

      <section className="flex flex-col items-center scans-section-with-actions">
        {!ocrGenerated && (
          <div className="flex items-center justify-center pt-2">
            {buttons.map((button) => (
              <Button
                key={button.text}
                color={button.color}
                onClick={button.btnClick}
                disabled={button.disable}
                loading={button.loading}
              >
                {button.text}
              </Button>
            ))}
          </div>
        )}
        {noDocContent ? (
          <div className="my-4"> {noDocContent} </div>
        ) : isReceiptAttachmentLoading || isInvoiceAttachmentsLoading ? (
          <div className="w-auto h-[85vh] flex items-center justify-center">
            <span className="k-icon k-i-loading text-lg" />
          </div>
        ) : attachments?.length === 0 ? (
          <img src={NotAvailable} alt="Not Available" className="block mx-auto" />
        ) : (
          <>
            <DocumentCountTracker
              disableDecrement={selected <= 1}
              disableIncrement={selected >= attachments.length}
              total={attachments.length}
              currentSelected={selected}
              incrementFn={increment}
              decrementFn={decrement}
            />
            {isImageType ? (
              <Image
                src={`${validSignedUrl(attachments?.[selected - 1]?.signedUrl)}`}
                alt="preview"
                controls
                filename={attachments?.[selected - 1]?.originalFile}
                styles={{
                  width: `${width}px`,
                  maxHeight: `${height - 260}px`,
                }}
              />
            ) : (
              <PreviewPDF
                url={`${validSignedUrl(attachments?.[selected - 1]?.signedUrl)}`}
                height={`${height - 210}px`}
                filename={attachments?.[selected - 1]?.originalFile}
              />
            )}
          </>
        )}
      </section>
    </>
  );
};

export default React.memo(PreviewSection);
